// src/components/TicketManagement.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  getTrackedCampaignForSubEvent,
  TRACKED_SUB_EVENT_CAMPAIGNS,
} from '../constants/emailCampaigns';
import { TICKET_CATEGORIES } from '../constants/importPresets';
import {
  getDisplayName,
  getFirstName,
  getFreshlingDisplayName,
  getLastName,
  abbreviateDays,
} from '../utils/flyerDisplay';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import { DEFAULT_TICKET_LAYOUTS, FALLBACK_LAYOUT } from '../constants/ticketLayouts';
import { openPrintWindow } from '../utils/printUtils';
import {
  copyText,
  buildTsvTable,
  formatEmailList,
  sanitizeEmailForClipboard,
} from '../utils/clipboardUtils';
import { HIGH_ALTITUDE_LABEL, isDemandOnlyUsageLabel, purchasedForUsageLabel } from '../utils/ticketUsage';
import './TicketManagement.css';

const VIEW_MODES = {
  SUB_EVENTS: 'subEvents',
  FULL_MANIFEST: 'fullManifest',
};

/** Full manifest: purchased + used per usage-tracked label (HA: purchased only). */
const FULL_MANIFEST_TICKET_COLS = [
  { label: 'High Altitude', short: 'HA', cols: ['purchased'] },
  { label: 'Saturday Competition', short: 'Sat', cols: ['purchased', 'used'] },
  { label: 'Jet', short: 'Jet', cols: ['purchased', 'used'] },
];

const EXPORT_COLUMNS = [
  { id: 'name', label: 'Name', default: true },
  { id: 'firstName', label: 'First name', default: false },
  { id: 'lastName', label: 'Last name', default: false },
  { id: 'email', label: 'Email', default: true },
  { id: 'team', label: 'Team (paired)', default: true },
  { id: 'type', label: 'Type', default: true },
  { id: 'days', label: 'Days', default: true },
  { id: 'sheetOrder', label: 'Sheet #', default: false },
  { id: 'tShirtSize', label: 'T-shirt size', default: false },
  { id: 'flagged', label: 'Flagged', default: true },
  { id: 'haPurchased', label: 'HA Purchased', default: false },
  { id: 'satUsed', label: 'Sat Used', default: false },
  { id: 'jetPurchased', label: 'Jet Purchased', default: false },
];

const DEFAULT_EXPORT_COLS = new Set(EXPORT_COLUMNS.filter((c) => c.default).map((c) => c.id));

const SUB_EVENT_EXPORT_COL_LABELS = {
  name: 'Name',
  email: 'Email',
  team: 'Team',
  days: 'Days',
  ticketCount: 'Tickets',
  phone: 'Phone',
  jumps: 'Skydives',
  tunnel: 'Tunnel',
  mentor: 'Mentor',
};

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function TicketManagement() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState('');

  const [flyers, setFlyers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const [viewMode, setViewMode] = useState(VIEW_MODES.SUB_EVENTS);

  // Sub-event ticket management state
  const [selectedLabel, setSelectedLabel] = useState('');
  const [subEventSearch, setSubEventSearch] = useState('');
  const [subEventSortBy, setSubEventSortBy] = useState('firstName'); // firstName | lastName

  // Full manifest state
  const [fullFilterType, setFullFilterType] = useState('all'); // all | freshling | mentor
  const [fullSearch, setFullSearch] = useState('');
  const [fullSortBy, setFullSortBy] = useState('name'); // firstName | lastName | name | email | team | type
  const [fullSortDir, setFullSortDir] = useState('asc'); // asc | desc
  const [fullHideCancelled, setFullHideCancelled] = useState(true);
  const [fullHideTest, setFullHideTest] = useState(true);
  const [fullHideWithdrawn, setFullHideWithdrawn] = useState(true);
  const [fullManifestCheckInFilter, setFullManifestCheckInFilter] = useState('all'); // all | flagged | unflagged
  const [exportColsModal, setExportColsModal] = useState(null); // null | 'print' | 'copy' (full manifest)
  const [exportCols, setExportCols] = useState(() => new Set(DEFAULT_EXPORT_COLS));
  const [subEventExportModal, setSubEventExportModal] = useState(null); // null | 'print' | 'copy'
  const [subEventExportCols, setSubEventExportCols] = useState(() => new Set());

  const navigate = useNavigate();

  // Email composer modal state
  const { emailComposerProps, openEmailComposer, closeEmailComposer } = useEmailComposer();

  const [emailCopiedKey, setEmailCopiedKey] = useState(null);

  useEffect(() => {
    if (!emailCopiedKey) return undefined;
    const id = window.setTimeout(() => setEmailCopiedKey(null), 1600);
    return () => window.clearTimeout(id);
  }, [emailCopiedKey]);

  // Fetch events once on mount
  useEffect(() => {
    let cancelled = false;
    async function loadEvents() {
      setEventsLoading(true);
      setEventsError('');
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length) {
          setSelectedEventId((prev) =>
            prev && list.some((e) => e.id === prev)
              ? prev
              : resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: list })
          );
        }
      } catch (e) {
        if (!cancelled) setEventsError(e.message || 'Failed to load events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    }

    loadEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch flyers + teams when event changes
  useEffect(() => {
    if (!selectedEventId) return;
    setDataLoading(true);
    setDataError('');
    (async () => {
      try {
        const [flyersSnap, teamsSnap] = await Promise.all([
          getDocs(
            query(
              collection(firestore, 'flyers'),
              where('eventId', '==', selectedEventId)
            )
          ),
          getDocs(
            query(
              collection(firestore, 'teams'),
              where('eventId', '==', selectedEventId)
            )
          ),
        ]);
        setFlyers(flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        setDataError(e.message || 'Failed to load manifest data.');
      } finally {
        setDataLoading(false);
      }
    })();
  }, [selectedEventId]);

  const currentEvent = useMemo(
    () => events.find((ev) => ev.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  const registrationStats = useMemo(() => {
    const total = flyers.filter((f) => f && !f.cancelled).length;
    const cancelled = flyers.filter((f) => f && f.cancelled).length;
    const mentors = flyers.filter((f) => f && !f.cancelled && f.type === 'mentor').length;
    const freshlings = flyers.filter((f) => f && !f.cancelled && f.type === 'freshling').length;
    return { total, cancelled, mentors, freshlings };
  }, [flyers]);

  // Resolve per-sub-event layout, optionally overridable via event.ticketLayouts in future.
  const ticketLayoutsByLabel = useMemo(() => {
    const base = { ...DEFAULT_TICKET_LAYOUTS };
    const eventLayouts =
      (currentEvent && currentEvent.ticketLayouts) || {};
    return { ...base, ...eventLayouts };
  }, [currentEvent]);

  // Sub-event rows from eventSummary.byDayTable (excluding Active Registrations + Cancelled).
  // Also ensure canopy rows appear if canopyThursday / canopyFriday aggregates exist.
  const subEventRows = useMemo(() => {
    const summary = currentEvent && currentEvent.eventSummary;
    const byDayTable =
      (summary &&
        Array.isArray(summary.byDayTable) &&
        summary.byDayTable) ||
      [];
    let rows = byDayTable.filter(
      (row) =>
        row &&
        typeof row.label === 'string' &&
        row.label !== 'Active Registrations' &&
        row.label !== 'Cancelled'
    );

    if (summary) {
      if (
        summary.canopyThursday > 0 &&
        !rows.some((r) => r.label === 'Canopy Thursday')
      ) {
        rows = rows.concat({
          label: 'Canopy Thursday',
          total: summary.canopyThursday,
        });
      }
      if (
        summary.canopyFriday > 0 &&
        !rows.some((r) => r.label === 'Canopy Friday')
      ) {
        rows = rows.concat({
          label: 'Canopy Friday',
          total: summary.canopyFriday,
        });
      }
    }

    return rows;
  }, [currentEvent]);

  // Keep selectedLabel in sync with available sub-event rows
  useEffect(() => {
    if (!subEventRows.length) {
      setSelectedLabel('');
      return;
    }
    if (!selectedLabel) {
      setSelectedLabel(subEventRows[0].label);
      return;
    }
    const exists = subEventRows.some((r) => r.label === selectedLabel);
    if (!exists) {
      setSelectedLabel(subEventRows[0].label);
    }
  }, [subEventRows, selectedLabel]);

  // Map freshling email -> assigned team & mentor from teams collection.
  const freshlingAssignmentsByEmail = useMemo(() => {
    const map = new Map();
    if (!teams.length) return map;

    const flyersByEmail = new Map();
    flyers.forEach((f) => {
      if (!f.email) return;
      flyersByEmail.set(String(f.email).toLowerCase(), f);
    });

    teams.forEach((t) => {
      const teamNumber = t.teamNumber || '';
      const mentorEmailRaw = (t.mentor || '').toString().trim();
      const mentorEmail = mentorEmailRaw.toLowerCase();
      let mentorName = mentorEmailRaw;
      if (mentorEmail && flyersByEmail.has(mentorEmail)) {
        const mentorFlyer = flyersByEmail.get(mentorEmail);
        mentorName =
          getDisplayName(mentorFlyer) ||
          mentorFlyer.name ||
          mentorFlyer.email ||
          mentorEmailRaw;
      }
      const freshlings = Array.isArray(t.freshlings) ? t.freshlings : [];
      freshlings.forEach((email) => {
        const key = (email || '').toString().trim().toLowerCase();
        if (!key) return;
        map.set(key, {
          teamNumber,
          mentorName,
        });
      });
    });

    return map;
  }, [teams, flyers]);

  const isUsageTrackedLabel = selectedLabel && TICKET_CATEGORIES.futureManifest.has(selectedLabel);
  const showUsedColumnForSubEvent =
    isUsageTrackedLabel && !isDemandOnlyUsageLabel(selectedLabel);

  // Sub-event registrants: manifest or usage-tracked purchased/used (HA: purchased demand only, like Jet)
  const subEventRegistrants = useMemo(() => {
    if (!selectedLabel) return [];
    const layout =
      ticketLayoutsByLabel[selectedLabel] || FALLBACK_LAYOUT;
    const search = (subEventSearch || '').trim().toLowerCase();
    const getLastName = (f) => {
      const last = (f.lastName || '').toString().trim();
      if (last) return last;
      const full = getFreshlingDisplayName(f) || f.name || '';
      const parts = full.trim().split(/\s+/);
      return parts.length > 1 ? parts[parts.length - 1] : '';
    };
    const usageTracked = TICKET_CATEGORIES.futureManifest.has(selectedLabel);
    const rows = flyers
      .filter((f) => {
        if (!f || f.type !== 'freshling' || f.cancelled) return false;
        const manifest = (f.manifestTickets && f.manifestTickets[selectedLabel]) || 0;
        const u = f.usage?.[selectedLabel] || {};
        const used = (u.used ?? 0) || 0;
        const purchasedTotal = purchasedForUsageLabel(f, selectedLabel);
        if (usageTracked) {
          if (isDemandOnlyUsageLabel(selectedLabel)) {
            return purchasedTotal > 0;
          }
          return purchasedTotal > 0 || used > 0;
        }
        return manifest > 0;
      })
      .map((f) => {
        const name =
          getFreshlingDisplayName(f) ||
          f.name ||
          f.email ||
          'Freshling';
        const email = f.email || '';
        const days = abbreviateDays(f.days) || '';
        const emailKey = (email || '').toLowerCase().trim();
        const assignment = freshlingAssignmentsByEmail.get(emailKey) || null;
        const assignedTeamLabel = assignment?.teamNumber
          ? `#${assignment.teamNumber}`
          : f.team || '';
        const mentor = assignment?.mentorName || '';
        const manifestCount = (f.manifestTickets && f.manifestTickets[selectedLabel]) || 0;
        const u = f.usage?.[selectedLabel] || {};
        const usedCount = (u.used ?? 0) || 0;
        const purchasedOnSite = purchasedForUsageLabel(f, selectedLabel);
        const ticketCount = manifestCount;
        const phone = f.phone || '';
        const jumps =
          f.jumpNumber != null && f.jumpNumber !== ''
            ? String(f.jumpNumber)
            : '';
        const tunnel = f.tunnel || '';
        const campaign = TRACKED_SUB_EVENT_CAMPAIGNS.find(
          (c) => c.label === selectedLabel
        );
        const emailSentForSubEvent =
          campaign &&
          !!((f.subEventEmailStatus || {})[campaign.label]?.[
            campaign.statusField
          ]);
        return {
          flyer: f,
          name,
          email,
          days,
          team: assignedTeamLabel,
          mentor,
          ticketCount,
          manifestCount,
          purchasedOnSite,
          usedCount,
          phone,
          jumps,
          tunnel,
          layout,
          emailSentForSubEvent: !!emailSentForSubEvent,
        };
      })
      .filter((row) => {
        if (!search) return true;
        const haystack = `${row.name} ${row.email} ${row.team}`.toLowerCase();
        return haystack.includes(search);
      });

    const sorted = [...rows].sort((a, b) => {
      let va = '';
      let vb = '';
      if (subEventSortBy === 'lastName') {
        va = getLastName(a.flyer) || '';
        vb = getLastName(b.flyer) || '';
      } else {
        va = getFirstName(a.flyer) || '';
        vb = getFirstName(b.flyer) || '';
      }
      return va.localeCompare(vb, undefined, { sensitivity: 'base' });
    });

    return sorted;
  }, [
    flyers,
    selectedLabel,
    subEventSearch,
    freshlingAssignmentsByEmail,
    ticketLayoutsByLabel,
    subEventSortBy,
  ]);

  const subEventUsageColumnSums = useMemo(() => {
    if (!isUsageTrackedLabel) return null;
    const sums = { purchased: 0, used: 0 };
    for (const r of subEventRegistrants) {
      sums.purchased += Number(r.purchasedOnSite) || 0;
      sums.used += Number(r.usedCount) || 0;
    }
    return sums;
  }, [isUsageTrackedLabel, subEventRegistrants]);

  const subEventExportColumnDefs = useMemo(() => {
    if (!selectedLabel) return [];
    const layout = ticketLayoutsByLabel[selectedLabel] || FALLBACK_LAYOUT;
    let cols = layout.columns || FALLBACK_LAYOUT.columns;
    const hideDaysTicket = [
      'Canopy Thursday',
      'Canopy Friday',
      'Water Training (Thur)',
      'Water Training (Fri)',
    ].includes(selectedLabel);
    if (hideDaysTicket) {
      cols = cols.filter((c) => c !== 'days' && c !== 'ticketCount');
    }
    const usageTracked = TICKET_CATEGORIES.futureManifest.has(selectedLabel);
    const out = [];
    for (const c of cols) {
      if (c === 'ticketCount' && usageTracked) {
        out.push({ id: 'purchasedOnSite', label: 'Purchased', default: true });
        if (!isDemandOnlyUsageLabel(selectedLabel)) {
          out.push({ id: 'usedCount', label: 'Used', default: true });
        }
      } else {
        out.push({
          id: c,
          label: SUB_EVENT_EXPORT_COL_LABELS[c] || c,
          default: true,
        });
      }
    }
    return out;
  }, [selectedLabel, ticketLayoutsByLabel]);

  useEffect(() => {
    if (!subEventExportModal) return;
    setSubEventExportCols(
      new Set(subEventExportColumnDefs.filter((c) => c.default).map((c) => c.id))
    );
  }, [subEventExportModal, subEventExportColumnDefs]);

  const flyersByEmail = useMemo(() => {
    const map = new Map();
    flyers.forEach((f) => {
      const email = (f.email || '').toLowerCase().trim();
      if (email) map.set(email, f);
    });
    return map;
  }, [flyers]);

  const teammatesByEmail = useMemo(() => {
    const map = new Map();
    if (!teams.length || !flyers.length) return map;

    teams.forEach((t) => {
      const memberEmails = new Set();
      const mentorEmail = (t.mentor || '').toString().trim().toLowerCase();
      if (mentorEmail) memberEmails.add(mentorEmail);
      (Array.isArray(t.freshlings) ? t.freshlings : []).forEach((e) => {
        const email = (e || '').toString().trim().toLowerCase();
        if (email) memberEmails.add(email);
      });
      if (!memberEmails.size) return;

      const members = Array.from(memberEmails)
        .map((email) => {
          const f = flyersByEmail.get(email);
          return f ? getDisplayName(f) || f.name || f.email || '' : '';
        })
        .filter(Boolean);

      if (!members.length) return;

      memberEmails.forEach((email) => {
        const others = members.filter((name, idx) => {
          const f = flyersByEmail.get(email);
          const selfName = f ? getDisplayName(f) || f.name || f.email || '' : '';
          return name !== selfName;
        });
        if (others.length) {
          map.set(email, others);
        }
      });
    });

    return map;
  }, [teams, flyersByEmail, flyers.length]);

  const teamLabelByEmail = useMemo(() => {
    const map = new Map();
    teams.forEach((t) => {
      const num = (t.teamNumber || '').toString().trim();
      const tName = (t.teamName || '').toString().trim();
      const label = tName ? `Team ${num} – ${tName}` : num ? `Team ${num}` : '';
      if (!label) return;
      const mentorEmail = (t.mentor || '').toString().trim().toLowerCase();
      if (mentorEmail) map.set(mentorEmail, label);
      (t.freshlings || []).forEach((e) => {
        const key = (e || '').toString().trim().toLowerCase();
        if (key) map.set(key, label);
      });
    });
    return map;
  }, [teams]);

  // Full manifest: all flyers for the event with filters/sorting
  const fullManifestRows = useMemo(() => {
    let list = flyers;
    if (fullHideCancelled) {
      list = list.filter((f) => !f.cancelled);
    }
    if (fullHideTest) {
      list = list.filter((f) => !f.test);
    }
    if (fullHideWithdrawn) {
      list = list.filter((f) => {
        const st = (f.status || '').toString().toLowerCase();
        const notes = (f.notes || '').toString().toLowerCase();
        if (st.includes('withdrawn') || notes.includes('withdrawn')) return false;
        if (st.includes('duplicate') || notes.includes('duplicate')) return false;
        if (f.type === 'mentor' && !st.includes('accept')) return false;
        return true;
      });
    }
    if (fullFilterType !== 'all') {
      list = list.filter((f) => f.type === fullFilterType);
    }
    if (fullManifestCheckInFilter === 'flagged') {
      list = list.filter((f) => f.manifestCheckInRequired === true);
    }
    if (fullManifestCheckInFilter === 'unflagged') {
      list = list.filter((f) => f.manifestCheckInRequired !== true);
    }
    const search = (fullSearch || '').trim().toLowerCase();
    if (search) {
      list = list.filter((f) => {
        const name = getDisplayName(f) || f.name || '';
        const email = f.email || '';
        const team = f.team || '';
        const haystack = `${name} ${email} ${team}`.toLowerCase();
        return haystack.includes(search);
      });
    }
    const dir = fullSortDir === 'asc' ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      if (fullSortBy === 'sheetOrder') {
        const na = parseInt(String(a.sheetOrder || '').trim(), 10);
        const nb = parseInt(String(b.sheetOrder || '').trim(), 10);
        const aValid = Number.isFinite(na);
        const bValid = Number.isFinite(nb);
        if (aValid && bValid) return dir * (na - nb);
        if (aValid) return -1;
        if (bValid) return 1;
        return 0;
      }
      let va = '';
      let vb = '';
      switch (fullSortBy) {
        case 'firstName':
          va = getFirstName(a) || '';
          vb = getFirstName(b) || '';
          break;
        case 'lastName':
          va = getLastName(a) || '';
          vb = getLastName(b) || '';
          break;
        case 'email':
          va = a.email || '';
          vb = b.email || '';
          break;
        case 'team':
          va = teamLabelByEmail.get((a.email || '').toLowerCase().trim()) || a.team || '';
          vb = teamLabelByEmail.get((b.email || '').toLowerCase().trim()) || b.team || '';
          break;
        case 'type':
          va = a.type || '';
          vb = b.type || '';
          break;
        case 'name':
        default:
          va = getDisplayName(a) || a.name || '';
          vb = getDisplayName(b) || b.name || '';
          break;
      }
      return dir * va.localeCompare(vb, undefined, { sensitivity: 'base' });
    });
    return sorted;
  }, [flyers, fullFilterType, fullManifestCheckInFilter, fullSearch, fullSortBy, fullSortDir, fullHideCancelled, fullHideTest, fullHideWithdrawn, teamLabelByEmail]);

  const handleEventChange = (e) => {
    setSelectedEventId(e.target.value);
  };

  function parseFinalRecipients(to, bcc) {
    const combined = [to || '', bcc || ''].join(',').trim();
    if (!combined) return new Set();
    return new Set(
      combined
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    );
  }

  const handleEmailSent = async ({
    selectedTemplate,
    context,
    to,
    bcc,
  }) => {
    if (!context) return;
    const finalRecipients = parseFinalRecipients(to, bcc);

    // Audit log is written once inside EmailComposerModal (emailSends).

    // Per-flyer tracking: only for tracked sub-event campaigns
    const campaign = getTrackedCampaignForSubEvent(
      context.subEventLabel,
      selectedTemplate
    );
    if (!campaign) return;

    const label = campaign.label;
    const statusField = campaign.statusField;
    const updatePath = `subEventEmailStatus.${label}.${statusField}`;

    if (context.type === 'subEvent' && Array.isArray(context.registrants)) {
      const emailToFlyer = new Map();
      context.registrants.forEach((f) => {
        const e = (f.email || '').trim().toLowerCase();
        if (e) emailToFlyer.set(e, f);
      });
      const toUpdate = [];
      finalRecipients.forEach((email) => {
        const flyer = emailToFlyer.get(email);
        if (flyer && flyer.id) toUpdate.push(flyer);
      });
      if (toUpdate.length === 0) return;
      try {
        const batch = writeBatch(firestore);
        toUpdate.forEach((f) => {
          batch.update(doc(firestore, 'flyers', f.id), {
            [updatePath]: serverTimestamp(),
          });
        });
        await batch.commit();
        const now = new Date();
        setFlyers((prev) =>
          prev.map((f) => {
            const inList = toUpdate.some((u) => u.id === f.id);
            if (!inList) return f;
            const next = { ...f };
            next.subEventEmailStatus = next.subEventEmailStatus || {};
            next.subEventEmailStatus[label] = {
              ...(next.subEventEmailStatus[label] || {}),
              [statusField]: now,
            };
            return next;
          })
        );
      } catch (err) {
        console.error('Failed to update flyer email status:', err);
      }
      return;
    }

    if (context.type === 'flyer' && context.flyer) {
      const flyer = context.flyer;
      const email = (flyer.email || '').trim().toLowerCase();
      if (!email || !finalRecipients.has(email)) return;
      try {
        await updateDoc(doc(firestore, 'flyers', flyer.id), {
          [updatePath]: serverTimestamp(),
        });
        const now = new Date();
        setFlyers((prev) =>
          prev.map((f) =>
            f.id !== flyer.id
              ? f
              : {
                  ...f,
                  subEventEmailStatus: {
                    ...(f.subEventEmailStatus || {}),
                    [label]: {
                      ...((f.subEventEmailStatus || {})[label] || {}),
                      [statusField]: now,
                    },
                  },
                }
          )
        );
      } catch (err) {
        console.error('Failed to update flyer email status:', err);
      }
    }
  };

  const handleSubEventEmailAll = () => {
    if (!selectedLabel || !subEventRegistrants.length) return;
    const emails = Array.from(
      new Set(
        subEventRegistrants
          .map((r) => (r.email || '').trim())
          .filter(Boolean)
      )
    );
    if (!emails.length) return;
    openEmailComposer({
      template:
        selectedLabel === 'After Party'
          ? 'after_party_desc_budget'
          : 'custom',
      to: '',
      bcc: emails.join(','),
      context: {
        type: 'subEvent',
        subEventLabel: selectedLabel,
        event: currentEvent,
        registrants: subEventRegistrants.map((r) => r.flyer),
      },
    });
  };

  const getSubEventExportCell = (r, colId) => {
    switch (colId) {
      case 'purchasedOnSite':
        return String(r.purchasedOnSite ?? '');
      case 'usedCount':
        return String(r.usedCount ?? '');
      case 'name':
        return r.name || '';
      case 'email':
        return r.email || '';
      case 'team':
        return r.team || '';
      case 'days':
        return r.days || '';
      case 'ticketCount':
        return String(r.ticketCount || '');
      case 'phone':
        return r.phone || '';
      case 'jumps':
        return r.jumps || '';
      case 'tunnel':
        return r.tunnel || '';
      case 'mentor':
        return r.mentor || '';
      default:
        return '';
    }
  };

  const resetSubEventExportCols = () => {
    setSubEventExportCols(
      new Set(subEventExportColumnDefs.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runSubEventExport = (selectedColumnIds) => {
    if (!subEventExportModal || !selectedLabel || !subEventRegistrants.length) return;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : subEventExportCols;
    const active = subEventExportColumnDefs.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const usageTracked = TICKET_CATEGORIES.futureManifest.has(selectedLabel);
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = subEventRegistrants.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = getSubEventExportCell(r, c.id);
      });
      return o;
    });
    const eventTitle = currentEvent ? currentEvent.title || '' : '';
    const heading = `${eventTitle ? `${eventTitle} – ` : ''}${selectedLabel} tickets`;
    if (subEventExportModal === 'copy') {
      let text = buildTsvTable(colMeta, rowRecords);
      if (usageTracked && subEventUsageColumnSums) {
        const totalRow = {};
        active.forEach((c) => {
          if (c.id === 'purchasedOnSite') totalRow[c.id] = String(subEventUsageColumnSums.purchased);
          else if (c.id === 'usedCount') totalRow[c.id] = String(subEventUsageColumnSums.used);
          else if (c.id === 'name') totalRow[c.id] = 'Total';
          else totalRow[c.id] = '';
        });
        const totalLine = active
          .map((c) => String(totalRow[c.id] ?? '').replace(/\r?\n/g, ' ').replace(/\t/g, ' '))
          .join('\t');
        text += `\n${totalLine}`;
      }
      copyText(text);
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const totalsFooter =
        usageTracked && subEventUsageColumnSums
          ? `<tfoot><tr>${active
              .map((c) => {
                if (c.id === 'purchasedOnSite') {
                  return `<td><strong>${escapeHtmlCell(String(subEventUsageColumnSums.purchased))}</strong></td>`;
                }
                if (c.id === 'usedCount') {
                  return `<td><strong>${escapeHtmlCell(String(subEventUsageColumnSums.used))}</strong></td>`;
                }
                if (c.id === 'name') return '<td><strong>Total</strong></td>';
                return '<td></td>';
              })
              .join('')}</tr></tfoot>`
          : '';
      const bodyHtml = `<main style="padding: 8px 12px;">
  <h1>${escapeHtmlCell(heading)}</h1>
  <p>Freshlings with tickets: ${subEventRegistrants.length}</p>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${rowsHtml}</tbody>${totalsFooter}
  </table>
</main>`;
      openPrintWindow(heading, bodyHtml, tablePrintCss);
    }
    setSubEventExportModal(null);
  };

  const tablePrintCss = `
    body {
      margin: 0.4in;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
      font-size: 13px;
    }
    th,
    td {
      padding: 5px 8px;
      border: 1px solid #e5e7eb;
      text-align: left;
    }
    th {
      background: #f3f4f6;
    }
    tbody tr:nth-child(even),
    tbody tr:nth-of-type(even) {
      background-color: #e5e7eb;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    tr.flagged-row {
      background-color: #fef3c7 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    tfoot td {
      border-top: 2px solid #9ca3af;
      background: #f3f4f6;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @media print {
      body {
        margin: 0.4in;
        font-size: 13px;
      }
      tr.flagged-row {
        background-color: #fef3c7 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `;

  const renderHeader = () => (
    <div className="ticket-management-header">
      <div className="tm-header-left">
        <h2>Ticket &amp; Manifest Management</h2>
        <div className="tm-stats">
          <div className="tm-stat">
            <div className="tm-stat-value">{registrationStats.total}</div>
            <div className="tm-stat-label">Registrations</div>
          </div>
          <div className="tm-stat">
            <div className="tm-stat-value">{registrationStats.freshlings}</div>
            <div className="tm-stat-label">Freshlings</div>
          </div>
          <div className="tm-stat">
            <div className="tm-stat-value">{registrationStats.mentors}</div>
            <div className="tm-stat-label">Mentors</div>
          </div>
          {registrationStats.cancelled > 0 && (
            <div className="tm-stat tm-stat--muted" title="Cancelled registrations">
              <div className="tm-stat-value">{registrationStats.cancelled}</div>
              <div className="tm-stat-label">Cancelled</div>
            </div>
          )}
        </div>
        {(eventsError || dataError) && (
          <div className="tm-alerts">
            {eventsError && <span className="tm-alert tm-alert--error">{eventsError}</span>}
            {dataError && <span className="tm-alert tm-alert--error">{dataError}</span>}
          </div>
        )}
      </div>
      <div className="ticket-management-controls">
        <label>
          Event:{' '}
          <select
            value={selectedEventId}
            onChange={handleEventChange}
          >
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title}
              </option>
            ))}
          </select>
        </label>
        <div className="ticket-management-view-toggle">
          <button
            type="button"
            className={
              viewMode === VIEW_MODES.SUB_EVENTS
                ? 'tm-view-btn tm-view-btn--active'
                : 'tm-view-btn'
            }
            onClick={() => setViewMode(VIEW_MODES.SUB_EVENTS)}
          >
            Sub-event tickets
          </button>
          <button
            type="button"
            className={
              viewMode === VIEW_MODES.FULL_MANIFEST
                ? 'tm-view-btn tm-view-btn--active'
                : 'tm-view-btn'
            }
            onClick={() => setViewMode(VIEW_MODES.FULL_MANIFEST)}
          >
            Full manifest
          </button>
        </div>
      </div>
    </div>
  );

  const renderSubEventSidebar = () => (
    <aside className="ticket-management-sidebar">
      <h3 className="tm-sidebar-title">Sub-events</h3>
      {(!currentEvent || !subEventRows.length) && (
        <p className="tm-sidebar-empty">
          No ticket categories yet. Import an event summary first.
        </p>
      )}
      <ul className="tm-sub-event-list">
        {subEventRows.map((row) => (
          <li key={row.label}>
            <button
              type="button"
              className={
                row.label === selectedLabel
                  ? 'tm-sub-event-btn tm-sub-event-btn--active'
                  : 'tm-sub-event-btn'
              }
              onClick={() => setSelectedLabel(row.label)}
            >
              <span className="tm-sub-event-label">{row.label}</span>
              <span className="tm-sub-event-count">
                {row.total ?? '—'}
                {registrationStats.freshlings > 0 && Number.isFinite(row.total) && (
                  <span className="tm-sub-event-pct">
                    {' '}
                    ({Math.round((row.total / registrationStats.freshlings) * 100)}%)
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );

  const currentSubEventLayout =
    (selectedLabel && ticketLayoutsByLabel[selectedLabel]) ||
    FALLBACK_LAYOUT;
  const subEventColumns =
    (currentSubEventLayout && currentSubEventLayout.columns) ||
    FALLBACK_LAYOUT.columns;
  const trackedCampaignForLabel = selectedLabel
    ? TRACKED_SUB_EVENT_CAMPAIGNS.find((c) => c.label === selectedLabel)
    : null;

  const renderSubEventMain = () => (
    <section className="ticket-management-main">
      <div className="tm-toolbar">
        <div className="tm-toolbar-left">
          <h3 className="tm-section-title">
            {selectedLabel || 'Sub-event tickets'}
          </h3>
          <p className="tm-section-subtitle">
            Freshlings with tickets for this sub-event, based on the Event Manifest.
          </p>
        </div>
        <div className="tm-toolbar-right">
          <button
            type="button"
            onClick={handleSubEventEmailAll}
            disabled={!subEventRegistrants.length}
          >
            Email all
          </button>
          <AdminCopyMenu
            disabled={!subEventRegistrants.length}
            className="tm-toolbar-copy-menu"
            items={[
              {
                label: 'Emails (comma-separated)',
                onSelect: async () => {
                  const emails = Array.from(
                    new Set(
                      subEventRegistrants
                        .map((r) => (r.email || '').trim())
                        .filter(Boolean)
                    )
                  );
                  if (!emails.length) return;
                  await copyText(formatEmailList(emails, 'comma'));
                },
              },
              {
                label: 'Emails (one per line)',
                onSelect: async () => {
                  const emails = Array.from(
                    new Set(
                      subEventRegistrants
                        .map((r) => (r.email || '').trim())
                        .filter(Boolean)
                    )
                  );
                  if (!emails.length) return;
                  await copyText(formatEmailList(emails, 'newline'));
                },
              },
              {
                label: 'Copy table…',
                showCopiedFeedback: false,
                onSelect: () => setSubEventExportModal('copy'),
              },
            ]}
          />
          <button
            type="button"
            className="btn-fm-print-outline"
            onClick={() => setSubEventExportModal('print')}
            disabled={!subEventRegistrants.length}
          >
            Print table
          </button>
          <label className="tm-sort-control">
            Sort:{' '}
            <select
              value={subEventSortBy}
              onChange={(e) => setSubEventSortBy(e.target.value)}
            >
              <option value="firstName">First name</option>
              <option value="lastName">Last name</option>
            </select>
          </label>
          <input
            type="text"
            placeholder="Search name, email, or team…"
            value={subEventSearch}
            onChange={(e) => setSubEventSearch(e.target.value)}
          />
        </div>
      </div>
      {dataLoading ? (
        <p>Loading tickets…</p>
      ) : !selectedLabel ? (
        <p>Select a sub-event on the left to see registrants.</p>
      ) : !subEventRegistrants.length ? (
        <p>No freshlings found for this sub-event.</p>
      ) : (
        <div className="tm-table-container">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Actions</th>
                {trackedCampaignForLabel && <th>Emailed</th>}
                {subEventColumns.map((col) => {
                  if (col === 'ticketCount' && isUsageTrackedLabel) {
                    return (
                      <React.Fragment key={col}>
                        <th>Purchased</th>
                        {showUsedColumnForSubEvent && <th>Used</th>}
                      </React.Fragment>
                    );
                  }
                  let label = col;
                  if (col === 'ticketCount') label = 'Tickets';
                  if (col === 'jumps') label = 'Jumps';
                  if (col === 'tunnel') label = 'Tunnel';
                  if (col === 'mentor') label = 'Mentor';
                  return <th key={col}>{label}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {subEventRegistrants.map((row) => (
                <tr
                  key={row.flyer.id}
                  className={
                    trackedCampaignForLabel && !row.emailSentForSubEvent
                      ? 'tm-row-not-emailed'
                      : undefined
                  }
                >
                  <td>
                    {row.email && (
                      <div className="tm-row-actions">
                        <button
                          type="button"
                          className="tm-icon-action--compose"
                          title="Email"
                          onClick={() =>
                            openEmailComposer({
                              template:
                                selectedLabel === 'After Party'
                                  ? 'after_party_desc_budget'
                                  : 'custom',
                              to: row.email,
                              bcc: '',
                              context: {
                                type: 'flyer',
                                subEventLabel: selectedLabel,
                                event: currentEvent,
                                flyer: row.flyer,
                              },
                            })
                          }
                        >
                          ✉
                        </button>
                        <button
                          type="button"
                          className="tm-icon-action--neutral"
                          title={
                            emailCopiedKey === `sub:${row.flyer.id}` ? 'Copied!' : 'Copy email'
                          }
                          onClick={async () => {
                            const plain = sanitizeEmailForClipboard(row.email);
                            try {
                              await copyText(plain, {
                                onSuccess: () => setEmailCopiedKey(`sub:${row.flyer.id}`),
                              });
                            } catch (err) {
                              console.error('Copy failed:', err);
                            }
                          }}
                        >
                          ⧉
                        </button>
                      </div>
                    )}
                  </td>
                  {trackedCampaignForLabel && (
                    <td
                      className="tm-email-status"
                      title={
                        row.emailSentForSubEvent
                          ? 'Received this sub-event email'
                          : 'Not yet emailed'
                      }
                    >
                      {row.emailSentForSubEvent ? '✓' : '—'}
                    </td>
                  )}
                  {subEventColumns.map((col) => {
                    if (col === 'ticketCount' && isUsageTrackedLabel) {
                      return (
                        <React.Fragment key={col}>
                          <td>{row.purchasedOnSite ?? ''}</td>
                          {showUsedColumnForSubEvent && <td>{row.usedCount ?? ''}</td>}
                        </React.Fragment>
                      );
                    }
                    let value = '';
                    switch (col) {
                      case 'name':
                        value = row.name || '';
                        break;
                      case 'email':
                        value = row.email || '';
                        break;
                      case 'team':
                        value = row.team || '—';
                        break;
                      case 'mentor':
                        value = row.mentor || '—';
                        break;
                      case 'days':
                        value = row.days || '—';
                        break;
                      case 'ticketCount':
                        value = row.ticketCount || '';
                        break;
                      case 'phone':
                        value = row.phone || '';
                        break;
                      case 'jumps':
                        value = row.jumps || '';
                        break;
                      case 'tunnel':
                        value = row.tunnel || '';
                        break;
                      default:
                        value = '';
                    }
                    return <td key={col}>{value}</td>;
                  })}
                </tr>
              ))}
            </tbody>
            {isUsageTrackedLabel && subEventUsageColumnSums && (
              <tfoot>
                <tr className="tm-table-foot">
                  <td className="tm-table-foot-actions" aria-hidden />
                  {trackedCampaignForLabel && <td className="tm-table-foot-actions" aria-hidden />}
                  {subEventColumns.map((col) => {
                    if (col === 'ticketCount' && isUsageTrackedLabel) {
                      const s = subEventUsageColumnSums;
                      return (
                        <React.Fragment key={`tf-${col}`}>
                          <td className="tm-num tm-table-foot-sum">{s.purchased}</td>
                          {showUsedColumnForSubEvent && (
                            <td className="tm-num tm-table-foot-sum">{s.used}</td>
                          )}
                        </React.Fragment>
                      );
                    }
                    if (col === 'name') {
                      return (
                        <td key={`tf-${col}`} className="tm-table-foot-label">
                          Total
                        </td>
                      );
                    }
                    return <td key={`tf-${col}`} className="tm-table-foot-empty" aria-hidden />;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
          <p className="tm-count">
            {subEventRegistrants.length} freshlings with tickets
          </p>
        </div>
      )}
    </section>
  );

  const getExportColValue = (f, colId) => {
    const emailKey = (f.email || '').toLowerCase().trim();
    const manifest = f.manifestTickets || {};
    const usage = f.usage || {};
    switch (colId) {
      case 'name': return getDisplayName(f) || f.name || f.email || 'Flyer';
      case 'firstName': return getFirstName(f) || '';
      case 'lastName': return getLastName(f) || '';
      case 'email': return f.email || '';
      case 'team': return teamLabelByEmail.get(emailKey) || f.team || '';
      case 'type': return f.type || '';
      case 'days': return abbreviateDays(f.days) || '';
      case 'sheetOrder': return f.type === 'freshling' ? String(f.sheetOrder || '') : '';
      case 'tShirtSize': {
        const sz = (f.tShirtAllocated || f.tShirtRequested || f.tShirtSize || '').toString().trim();
        return sz ? sz.toUpperCase() : '';
      }
      case 'flagged': return f.manifestCheckInRequired === true ? 'Flagged' : '';
      case 'haPurchased':
        return String(purchasedForUsageLabel(f, HIGH_ALTITUDE_LABEL));
      case 'satUsed': return String(usage['Saturday Competition']?.used ?? 0);
      case 'jetPurchased': {
        const j = 'Jet';
        const u = usage[j];
        const p =
          typeof u?.purchased === 'number' && !Number.isNaN(u.purchased)
            ? u.purchased
            : manifest[j] || 0;
        return String(p);
      }
      default: return '';
    }
  };

  const executeExport = (mode, selectedColumnIds) => {
    if (!fullManifestRows.length) return;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : exportCols;
    const activeCols = EXPORT_COLUMNS.filter((c) => colSet.has(c.id));
    if (!activeCols.length) return;

    if (mode === 'copy') {
      const header = activeCols.map((c) => c.label).join('\t');
      const rows = fullManifestRows.map((f) =>
        activeCols.map((c) => getExportColValue(f, c.id)).join('\t')
      );
      copyText([header, ...rows].join('\n'));
    } else {
      const eventTitle = currentEvent ? currentEvent.title || '' : '';
      const heading = `${eventTitle || 'Event'} – Full manifest`;
      const headerCells = activeCols.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = fullManifestRows
        .map((f) => {
          const flagged = f.manifestCheckInRequired === true;
          const rowClass = flagged && colSet.has('flagged') ? ' class="flagged-row"' : '';
          const cells = activeCols
            .map((c) => `<td>${escapeHtmlCell(getExportColValue(f, c.id))}</td>`)
            .join('');
          return `<tr${rowClass}>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding: 8px 12px;">
  <h1>${heading}</h1>
  <p>Total flyers: ${fullManifestRows.length}</p>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
</main>`;
      openPrintWindow(heading, bodyHtml, tablePrintCss);
    }
    setExportColsModal(null);
  };

  const renderFullManifest = () => (
    <section className="ticket-management-main ticket-management-main--full">
      <div className="tm-toolbar">
        <div className="tm-toolbar-left">
          <h3 className="tm-section-title">Full manifest</h3>
          <p className="tm-section-subtitle">
            All flyers for this event, including mentors and freshlings.
          </p>
        </div>
        <div className="tm-toolbar-right tm-toolbar-right--full">
          <label>
            Type:{' '}
            <select
              value={fullFilterType}
              onChange={(e) => setFullFilterType(e.target.value)}
            >
              <option value="all">All</option>
              <option value="freshling">Freshlings</option>
              <option value="mentor">Mentors</option>
            </select>
          </label>
          <label>
            Manifest check-in:{' '}
            <select
              value={fullManifestCheckInFilter}
              onChange={(e) => setFullManifestCheckInFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="flagged">Flagged only</option>
              <option value="unflagged">Unflagged only</option>
            </select>
          </label>
          <label className="tm-filter-checkbox">
            <input
              type="checkbox"
              checked={fullHideCancelled}
              onChange={(e) => setFullHideCancelled(e.target.checked)}
            />
            <span>Hide cancelled</span>
          </label>
          <label className="tm-filter-checkbox">
            <input
              type="checkbox"
              checked={fullHideTest}
              onChange={(e) => setFullHideTest(e.target.checked)}
            />
            <span>Hide test</span>
          </label>
          <label className="tm-filter-checkbox">
            <input
              type="checkbox"
              checked={fullHideWithdrawn}
              onChange={(e) => setFullHideWithdrawn(e.target.checked)}
            />
            <span>Hide withdrawn/dupes</span>
          </label>
          <label>
            Sort:{' '}
            <select
              value={fullSortBy}
              onChange={(e) => setFullSortBy(e.target.value)}
            >
              <option value="firstName">First name</option>
              <option value="lastName">Last name</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="team">Team</option>
              <option value="type">Type</option>
              <option value="sheetOrder">Sheet #</option>
            </select>
            <select
              value={fullSortDir}
              onChange={(e) => setFullSortDir(e.target.value)}
            >
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() =>
              navigate(
                `/admin/printables${selectedEventId ? `?eventId=${encodeURIComponent(selectedEventId)}` : ''}`
              )
            }
          >
            Printables
          </button>
          <button
            type="button"
            onClick={() => {
              if (!fullManifestRows.length) return;
              const emails = Array.from(
                new Set(
                  fullManifestRows
                    .map((f) => (f.email || '').trim())
                    .filter(Boolean)
                )
              );
              if (!emails.length) return;
              openEmailComposer({
                template: 'custom',
                to: '',
                bcc: emails.join(','),
                context: {
                  type: 'manifest',
                  event: currentEvent,
                },
              });
            }}
            disabled={!fullManifestRows.length}
          >
            Email all
          </button>
          <AdminCopyMenu
            disabled={!fullManifestRows.length}
            className="tm-toolbar-copy-menu"
            items={[
              {
                label: 'Emails (comma-separated)',
                onSelect: async () => {
                  if (!fullManifestRows.length) return;
                  const emails = Array.from(
                    new Set(
                      fullManifestRows
                        .map((f) => (f.email || '').trim())
                        .filter(Boolean)
                    )
                  );
                  if (!emails.length) return;
                  await copyText(formatEmailList(emails, 'comma'));
                },
              },
              {
                label: 'Emails (one per line)',
                onSelect: async () => {
                  if (!fullManifestRows.length) return;
                  const emails = Array.from(
                    new Set(
                      fullManifestRows
                        .map((f) => (f.email || '').trim())
                        .filter(Boolean)
                    )
                  );
                  if (!emails.length) return;
                  await copyText(formatEmailList(emails, 'newline'));
                },
              },
              {
                label: 'Check-in emails (comma)',
                disabled: !fullManifestRows.some(
                  (f) => f.manifestCheckInRequired === true
                ),
                onSelect: async () => {
                  const emails = Array.from(
                    new Set(
                      fullManifestRows
                        .filter((f) => f.manifestCheckInRequired === true)
                        .map((f) => (f.email || '').trim())
                        .filter(Boolean)
                    )
                  );
                  if (!emails.length) return;
                  await copyText(formatEmailList(emails, 'comma'));
                },
              },
              {
                label: 'Names (one per line)',
                onSelect: async () => {
                  if (!fullManifestRows.length) return;
                  const names = fullManifestRows.map(
                    (f) => getDisplayName(f) || f.name || f.email || 'Flyer'
                  );
                  await copyText(names.join('\n'));
                },
              },
              {
                label: 'First & last (TSV)',
                onSelect: async () => {
                  if (!fullManifestRows.length) return;
                  const header = 'First name\tLast name';
                  const rows = fullManifestRows.map((f) =>
                    [getFirstName(f), getLastName(f)].join('\t')
                  );
                  await copyText([header, ...rows].join('\n'));
                },
              },
              {
                label: 'Copy table…',
                showCopiedFeedback: false,
                onSelect: async () => {
                  setExportColsModal('copy');
                },
              },
            ]}
          />
          <button
            type="button"
            className="btn-fm-print-outline"
            onClick={() => setExportColsModal('print')}
            disabled={!fullManifestRows.length}
          >
            Print table
          </button>
          <input
            type="text"
            placeholder="Search name, email, or team…"
            value={fullSearch}
            onChange={(e) => setFullSearch(e.target.value)}
          />
        </div>
      </div>
      {dataLoading ? (
        <p>Loading manifest…</p>
      ) : !fullManifestRows.length ? (
        <p>No flyers found for this event.</p>
      ) : (
        <div className="tm-table-container">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Name</th>
                <th>Email</th>
                <th>Team</th>
                <th>Type</th>
                <th>Days</th>
                <th className="tm-num" title="Freshling sheet row number">Sheet #</th>
                <th>Flagged</th>
                {FULL_MANIFEST_TICKET_COLS.map(({ label, short, cols }) => (
                  <React.Fragment key={label}>
                    {cols.includes('purchased') && <th className="tm-num" title={`${label} purchased total`}>{short} Purchased</th>}
                    {cols.includes('used') && <th className="tm-num" title={`${label} used`}>{short} Used</th>}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {fullManifestRows.map((f) => {
                const name = getDisplayName(f) || f.name || f.email || 'Flyer';
                const days = abbreviateDays(f.days) || '';
                const emailKey = (f.email || '').toLowerCase().trim();
                const teammates = emailKey ? teammatesByEmail.get(emailKey) || [] : [];
                const usage = f.usage || {};
                return (
                  <tr key={f.id}>
                    <td>
                      {f.email && (
                        <div className="tm-row-actions">
                          <button
                            type="button"
                            className="tm-icon-action--compose"
                            title="Email"
                            onClick={() =>
                              openEmailComposer({
                                template: 'custom',
                                to: f.email,
                                bcc: '',
                                context: {
                                  type: 'manifest',
                                  event: currentEvent,
                                  flyer: f,
                                },
                              })
                            }
                          >
                            ✉
                          </button>
                          <button
                            type="button"
                            className="tm-icon-action--neutral"
                            title={
                              emailCopiedKey === `full:${f.id}` ? 'Copied!' : 'Copy email'
                            }
                            onClick={async () => {
                              const plain = sanitizeEmailForClipboard(f.email);
                              try {
                                await copyText(plain, {
                                  onSuccess: () => setEmailCopiedKey(`full:${f.id}`),
                                });
                              } catch (err) {
                                console.error('Copy failed:', err);
                              }
                            }}
                          >
                            ⧉
                          </button>
                        </div>
                      )}
                    </td>
                    <td>{name}</td>
                    <td>{f.email || '—'}</td>
                    <td>
                      {teamLabelByEmail.get(emailKey) || f.team || (teammates.length === 0 ? '—' : '')}
                      {teammates.length > 0 && (
                        <div className="tm-team-teammates">
                          {teammates.join(', ')}
                        </div>
                      )}
                    </td>
                    <td>{f.type || '—'}</td>
                    <td>{days || '—'}</td>
                    <td className="tm-num">{f.type === 'freshling' ? (f.sheetOrder || '—') : '—'}</td>
                    <td>{f.manifestCheckInRequired === true ? 'Flagged' : '—'}</td>
                    {FULL_MANIFEST_TICKET_COLS.map(({ label, cols }) => {
                      const purchasedVal = purchasedForUsageLabel(f, label);
                      const usedCount = usage[label]?.used ?? 0;
                      return (
                        <React.Fragment key={label}>
                          {cols.includes('purchased') && <td className="tm-num">{purchasedVal || '—'}</td>}
                          {cols.includes('used') && <td className="tm-num">{usedCount || '—'}</td>}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="tm-count">
            {fullManifestRows.length} flyer{fullManifestRows.length === 1 ? '' : 's'}
          </p>
        </div>
      )}
    </section>
  );

  if (eventsLoading && !events.length) {
    return (
      <div className="ticket-management">
        <h2>Ticket &amp; Manifest Management</h2>
        <p>Loading events…</p>
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="ticket-management">
        <h2>Ticket &amp; Manifest Management</h2>
        <p>No events found. Create an event first.</p>
      </div>
    );
  }

  return (
    <div className="ticket-management">
      {renderHeader()}
      <div className="ticket-management-body">
        {viewMode === VIEW_MODES.SUB_EVENTS && (
          <>
            {renderSubEventSidebar()}
            {renderSubEventMain()}
          </>
        )}
        {viewMode === VIEW_MODES.FULL_MANIFEST && renderFullManifest()}
      </div>
      {subEventExportModal && subEventExportColumnDefs.length > 0 && (
        <AdminColumnExportModal
          mode={subEventExportModal}
          columns={subEventExportColumnDefs}
          selectedIds={subEventExportCols}
          onChangeSelected={setSubEventExportCols}
          onCancel={() => setSubEventExportModal(null)}
          onConfirm={runSubEventExport}
          onResetDefaults={resetSubEventExportCols}
        />
      )}
      {exportColsModal && (
        <AdminColumnExportModal
          mode={exportColsModal}
          columns={EXPORT_COLUMNS}
          selectedIds={exportCols}
          onChangeSelected={setExportCols}
          onCancel={() => setExportColsModal(null)}
          onConfirm={(selectedColumnIds) => executeExport(exportColsModal, selectedColumnIds)}
          onResetDefaults={() => setExportCols(new Set(DEFAULT_EXPORT_COLS))}
        />
      )}
      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
        onEmailSent={handleEmailSent}
      />
    </div>
  );
}

export default TicketManagement;

